import { ref, get, child, update } from "firebase/database";
import { database } from "./firebase-config.js";

// Email keys in RTDB sanitise '.' -> '&' (see firebase-database.js).
export const sanitiseEmailKey = (email) =>
  String(email || "").replace(/\./g, "&");

// Coerce an RTDB value that may be an array, a sparse-array-as-object, or
// missing into a plain string array. Filters out falsy entries.
const coerceToStringArray = (value) => {
  if (value == null) return [];
  const list = Array.isArray(value) ? value : Object.values(value);
  return list.filter((v) => typeof v === "string" && v.length > 0);
};

// Coerce an RTDB value that may be a sparse array or array-as-object
// into a dense object array. Filters out null/undefined slots.
const coerceToObjectArray = (value) => {
  if (value == null) return [];
  const list = Array.isArray(value) ? value : Object.values(value);
  return list.filter((v) => v != null && typeof v === "object");
};

// Returns the list of business IDs the given user is an employee of.
// iOS parity: reads `employees/<sanitised-email>` and tolerates both
// array and sparse-array-as-object shapes. Returns [] if missing.
export const getEmployersForUser = async (email) => {
  if (!email) return [];
  try {
    const employeesRef = ref(database, `employees/${sanitiseEmailKey(email)}`);
    const snapshot = await get(employeesRef);
    if (!snapshot.exists()) return [];
    return coerceToStringArray(snapshot.val());
  } catch (error) {
    console.warn("Error fetching employers for user:", error);
    return [];
  }
};

// Returns the public display name for a business, or "" if not found.
export const getBusinessDisplayName = async (businessId) => {
  if (!businessId) return "";
  try {
    const dbRef = ref(database);
    const snapshot = await get(
      child(dbRef, `businesses/${businessId}/rewardsCardData/displayName`)
    );
    if (!snapshot.exists()) return "";
    const value = snapshot.val();
    return typeof value === "string" ? value : "";
  } catch (error) {
    console.warn("Error fetching business display name:", error);
    return "";
  }
};

// How many consent reads the export keeps in flight at once. A business with
// thousands of customers would otherwise open every read in a single burst.
const CONSENT_READ_CONCURRENCY = 50;

// Marketing email consent for a batch of customers, as a Map from the dotted
// email the caller passed in to true / false / undefined. Consent lives on the
// customer side of the graph (customers/<email>/businessesRegisteredWith/<id>/
// allowMarketingEmails) and the iOS operator app can flip it after enrolment,
// so the `businesses/<id>` snapshot the dashboard already holds cannot answer
// this. `undefined` means no flag was found OR the read failed - one
// unreadable customer must never sink a whole export, and "we don't know" is
// deliberately kept apart from a recorded "no".
export const getMarketingConsentForCustomers = async (businessId, emails) => {
  const consentByEmail = new Map();
  if (!businessId) return consentByEmail;
  const list = Array.from(new Set(coerceToStringArray(emails)));

  const readOne = async (email) => {
    try {
      const snapshot = await get(
        ref(
          database,
          `customers/${sanitiseEmailKey(email)}/businessesRegisteredWith/${businessId}/allowMarketingEmails`
        )
      );
      const value = snapshot.exists() ? snapshot.val() : undefined;
      consentByEmail.set(email, typeof value === "boolean" ? value : undefined);
    } catch (error) {
      consentByEmail.set(email, undefined);
    }
  };

  for (let i = 0; i < list.length; i += CONSENT_READ_CONCURRENCY) {
    await Promise.all(
      list.slice(i, i + CONSENT_READ_CONCURRENCY).map(readOne)
    );
  }
  return consentByEmail;
};

// Returns the full raw `businesses/<id>` snapshot value (an object with
// `customers`, `stamps`, `redeems`, `rewardsCardData`, `webDisplayData`,
// `employeesData`, `employees`, `isActive`, `thresholdForRedeeming`, …)
// or `null` if the business node is missing. Read errors are also
// surfaced as `null` so callers can render a friendly empty state.
export const getBusinessSnapshot = async (businessId) => {
  if (!businessId) return null;
  try {
    const businessRef = ref(database, `businesses/${businessId}`);
    const snapshot = await get(businessRef);
    if (!snapshot.exists()) return null;
    const value = snapshot.val();
    return value == null ? null : value;
  } catch (error) {
    console.warn("Error fetching business snapshot:", error);
    return null;
  }
};

// Returns the `employeesData` array for a business, normalised to a
// dense array of {name, email, role} entries. Sparse arrays coerced.
// This is the public DISPLAY view - only the three fields the dashboard
// needs. For writes (where iOS-side extras like `addedAt`, `aux*` must
// be preserved), use `getEmployeesDataRaw` instead.
export const getEmployeesData = async (businessId) => {
  if (!businessId) return [];
  try {
    const raw = await getEmployeesDataRaw(businessId);
    return raw.map((entry) => ({
      name: typeof entry.name === "string" ? entry.name : "",
      email: typeof entry.email === "string" ? entry.email : "",
      role: typeof entry.role === "string" ? entry.role : "Employee",
    }));
  } catch (error) {
    console.warn("Error fetching employees data:", error);
    return [];
  }
};

// Returns the raw `employeesData` array for a business, preserving every
// key on each row exactly as iOS wrote it. Used by the write helpers so
// fields the web dashboard doesn't know about (e.g. `addedAt`, `aux*`)
// survive a round-trip update.
//
// Non-object rows are filtered out, but object rows pass through untouched.
export const getEmployeesDataRaw = async (businessId) => {
  if (!businessId) return [];
  try {
    const employeesRef = ref(
      database,
      `businesses/${businessId}/employeesData`
    );
    const snapshot = await get(employeesRef);
    if (!snapshot.exists()) return [];
    return coerceToObjectArray(snapshot.val()).map((entry) => ({ ...entry }));
  } catch (error) {
    console.warn("Error fetching raw employees data:", error);
    return [];
  }
};

// Resolves the role for a given user inside a business. Defaults to
// "Employee" on any miss. Used by the dashboard shell + M5 management
// pages to gate edit affordances.
export const getCurrentUserRole = async (businessId, email) => {
  if (!businessId || !email) return "Employee";
  try {
    const list = await getEmployeesData(businessId);
    const lowered = String(email).toLowerCase();
    const match = list.find(
      (entry) =>
        entry &&
        typeof entry.email === "string" &&
        entry.email.toLowerCase() === lowered
    );
    return (match && match.role) || "Employee";
  } catch (error) {
    return "Employee";
  }
};

// Returns whether the given business is marked active in RTDB.
// Used by the Account page so we can show a "subscription pending"
// hint while Stripe is processing the first charge.
export const getBusinessIdActiveStatus = async (businessId) => {
  if (!businessId) return false;
  try {
    const dbRef = ref(database);
    const snapshot = await get(child(dbRef, `businesses/${businessId}/isActive`));
    if (!snapshot.exists()) return false;
    return snapshot.val() === true;
  } catch (error) {
    console.warn("Error fetching business active status:", error);
    return false;
  }
};

// ---------------------------------------------------------------------------
// Write helpers (M5)
//
// Each accepts a *partial* and uses `update()` so callers only need to send
// the fields they actually changed. This keeps the round-trip cheap and
// avoids accidentally clobbering iOS-only fields we don't know about.
// ---------------------------------------------------------------------------

// Sanitises a partial rewardsCardData payload so we never write `undefined`
// (Firebase rejects those) or top-level keys outside the rewards card slice.
const ALLOWED_REWARDS_CARD_KEYS = new Set([
  "id",
  "type",
  "displayName",
  "logoImageName",
  "backgroundColor",
  "foregroundColor",
  "labelColor",
  "passVersion",
  "auxiliaryFieldLabel",
  "auxiliaryFieldValue",
  // Monotonic cache-bust counter Android reads to refresh logo/stamp images.
  // Bumped on every branding/rewards-card save (see settingsHandler.js).
  "brandingVersion",
]);

const cleanRewardsCardPartial = (partial) => {
  const out = {};
  if (!partial || typeof partial !== "object") return out;
  for (const [key, value] of Object.entries(partial)) {
    if (!ALLOWED_REWARDS_CARD_KEYS.has(key)) continue;
    if (value === undefined) continue;
    out[key] = value;
  }
  return out;
};

const ALLOWED_WEB_DISPLAY_KEYS = new Set([
  "title",
  "subtitle",
  "backgroundColor",
  "textColor",
]);

const cleanWebDisplayPartial = (partial) => {
  const out = {};
  if (!partial || typeof partial !== "object") return out;
  for (const [key, value] of Object.entries(partial)) {
    if (!ALLOWED_WEB_DISPLAY_KEYS.has(key)) continue;
    if (value === undefined) continue;
    out[key] = value;
  }
  return out;
};

// Updates the `rewardsCardData` slice of a business (merge semantics) and,
// when supplied, also updates the top-level `thresholdForRedeeming` field -
// that lives outside `rewardsCardData` in the iOS-written payload.
//
// `partial` accepts:
//   { displayName?, backgroundColor?, foregroundColor?, labelColor?,
//     auxiliaryFieldLabel?, auxiliaryFieldValue?, logoImageName?,
//     thresholdForRedeeming? }
export const updateRewardsCardBranding = async (businessId, partial) => {
  if (!businessId) throw new Error("businessId is required");
  if (!partial || typeof partial !== "object") {
    throw new Error("partial is required");
  }

  const updates = {};
  const cardSlice = cleanRewardsCardPartial(partial);
  for (const [key, value] of Object.entries(cardSlice)) {
    updates[`businesses/${businessId}/rewardsCardData/${key}`] = value;
  }

  if (partial.thresholdForRedeeming !== undefined) {
    const numeric = Number(partial.thresholdForRedeeming);
    if (!Number.isFinite(numeric)) {
      throw new Error("thresholdForRedeeming must be a number");
    }
    updates[`businesses/${businessId}/thresholdForRedeeming`] = numeric;
  }

  if (Object.keys(updates).length === 0) return;
  await update(ref(database), updates);
};

// Updates the `webDisplayData` slice of a business with merge semantics.
// `partial` accepts: { title?, subtitle?, backgroundColor?, textColor? }
export const updateWebDisplayData = async (businessId, partial) => {
  if (!businessId) throw new Error("businessId is required");
  const cleaned = cleanWebDisplayPartial(partial);
  if (Object.keys(cleaned).length === 0) return;

  const updates = {};
  for (const [key, value] of Object.entries(cleaned)) {
    updates[`businesses/${businessId}/webDisplayData/${key}`] = value;
  }
  await update(ref(database), updates);
};

const ALLOWED_BUSINESS_PROFILE_KEYS = new Set([
  "displayName",
  // Free stamps new customers start with. Businesses that have never changed
  // it have no node at all, which every reader treats as 0.
  "startStampCount",
]);

const cleanBusinessProfilePartial = (partial) => {
  const out = {};
  if (!partial || typeof partial !== "object") return out;
  for (const [key, value] of Object.entries(partial)) {
    if (!ALLOWED_BUSINESS_PROFILE_KEYS.has(key)) continue;
    if (value === undefined) continue;
    out[key] = value;
  }
  return out;
};

// Updates top-level business profile fields that live OUTSIDE rewardsCardData/
// webDisplayData (displayName, startStampCount). Merge semantics, skips undefined.
// `partial` accepts: { displayName?, startStampCount? }
export const updateBusinessProfile = async (businessId, partial) => {
  if (!businessId) throw new Error("businessId is required");
  const cleaned = cleanBusinessProfilePartial(partial);
  if (Object.keys(cleaned).length === 0) return;

  const updates = {};
  for (const [key, value] of Object.entries(cleaned)) {
    updates[`businesses/${businessId}/${key}`] = value;
  }
  await update(ref(database), updates);
};

// Helper - read-modify-write preserve. Loads the existing employeesData
// array (RAW - keeps every key iOS may have written), mutates only the
// row matching `targetEmail` (case-insensitive) using `mutate(existingEntry)`,
// and returns the new dense array. Other rows are passed through
// completely unchanged so any iOS-only keys (e.g. `addedAt`, `aux*`) survive
// the round trip. The mutate callback is responsible for spreading
// `existingEntry` itself when it wants to preserve the target row's extras.
const buildEmployeesDataUpdate = async (businessId, targetEmail, mutate) => {
  const list = await getEmployeesDataRaw(businessId);
  const lowered = String(targetEmail || "").toLowerCase();
  let mutated = false;
  const next = [];

  for (const entry of list) {
    if (
      !mutated &&
      entry &&
      typeof entry.email === "string" &&
      entry.email.toLowerCase() === lowered
    ) {
      const updated = mutate(entry);
      if (updated !== null) next.push(updated);
      mutated = true;
    } else if (entry) {
      next.push(entry);
    }
  }

  if (!mutated) {
    const created = mutate(null);
    if (created !== null) next.push(created);
  }

  return next;
};

// Multi-path atomic write of the three iOS-parity employee paths.
//   1. businesses/<id>/employeesData     - read-modify-write the row
//   2. businesses/<id>/employees/<email> - set to true
//   3. employees/<email>                 - append <businessId> if missing
//
// `employee` is `{ name, email, role }`. Other keys present on the existing
// entry (iOS may store more than these three) are preserved.
export const saveEmployeeRecords = async (businessId, employee) => {
  if (!businessId) throw new Error("businessId is required");
  if (!employee || !employee.email) throw new Error("employee.email is required");

  const normalisedEmail = String(employee.email).toLowerCase();
  const sanitisedEmail = sanitiseEmailKey(normalisedEmail);
  const nextEmployee = {
    name: String(employee.name || ""),
    email: normalisedEmail,
    role: String(employee.role || "Employee"),
  };

  const nextList = await buildEmployeesDataUpdate(
    businessId,
    normalisedEmail,
    (existing) => ({
      ...(existing || {}),
      ...nextEmployee,
    })
  );

  // Read the global `employees/<email>` index and append businessId if it's
  // not already there. Preserve any pre-existing ids (multi-business users).
  let globalIds = [];
  try {
    const globalRef = ref(database, `employees/${sanitisedEmail}`);
    const snap = await get(globalRef);
    if (snap.exists()) {
      const value = snap.val();
      globalIds = Array.isArray(value)
        ? value.filter((v) => typeof v === "string" && v.length > 0)
        : Object.values(value || {}).filter(
            (v) => typeof v === "string" && v.length > 0
          );
    }
  } catch (e) {
    // Fall through with the empty list so the write below recovers state.
  }
  if (!globalIds.includes(businessId)) globalIds.push(businessId);

  const updates = {
    [`businesses/${businessId}/employeesData`]: nextList,
    [`businesses/${businessId}/employees/${sanitisedEmail}`]: true,
    [`employees/${sanitisedEmail}`]: globalIds,
  };
  await update(ref(database), updates);
};

// Atomic rename of an employee whose email address is changing. Builds a
// single multi-path `update()` so we never end up with orphan records or
// dual-sign-in if one of the writes fails.
//
// Touches:
//   1. businesses/<id>/employeesData                 - rebuild without
//      the previous-email row, with the new-email row added (raw round-trip
//      so iOS-only fields on every row survive; target-row extras are
//      preserved when the next row spreads the previous row).
//   2. businesses/<id>/employees/<sanitisedOldEmail> - null
//   3. businesses/<id>/employees/<sanitisedNewEmail> - true
//   4. employees/<sanitisedOldEmail>                 - list with this
//      businessId removed, or null if list becomes empty.
//   5. employees/<sanitisedNewEmail>                 - list with this
//      businessId appended (de-duped).
export const replaceEmployeeRecord = async (
  businessId,
  previousEmployee,
  nextEmployee
) => {
  if (!businessId) throw new Error("businessId is required");
  if (!previousEmployee || !previousEmployee.email) {
    throw new Error("previousEmployee.email is required");
  }
  if (!nextEmployee || !nextEmployee.email) {
    throw new Error("nextEmployee.email is required");
  }

  const previousEmail = String(previousEmployee.email).toLowerCase();
  const nextEmail = String(nextEmployee.email).toLowerCase();
  const sanitisedOld = sanitiseEmailKey(previousEmail);
  const sanitisedNew = sanitiseEmailKey(nextEmail);

  const nextEmployeeClean = {
    name: String(nextEmployee.name || ""),
    email: nextEmail,
    role: String(nextEmployee.role || "Employee"),
  };

  // Read the raw employeesData once. We rebuild a new array that drops
  // the previous-email row entirely and (re)adds the new-email row. Any
  // iOS-only keys on the previous-email row are forwarded onto the new
  // entry so we don't lose `addedAt` / `aux*` / etc. during a rename.
  const rawList = await getEmployeesDataRaw(businessId);
  let carriedExtras = null;
  const intermediate = [];
  for (const entry of rawList) {
    if (
      entry &&
      typeof entry.email === "string" &&
      entry.email.toLowerCase() === previousEmail
    ) {
      carriedExtras = entry;
      continue;
    }
    intermediate.push(entry);
  }

  let replaced = false;
  const nextList = intermediate.map((entry) => {
    if (
      !replaced &&
      entry &&
      typeof entry.email === "string" &&
      entry.email.toLowerCase() === nextEmail
    ) {
      replaced = true;
      return { ...entry, ...nextEmployeeClean };
    }
    return entry;
  });
  if (!replaced) {
    nextList.push({ ...(carriedExtras || {}), ...nextEmployeeClean });
  }

  // Read both global indexes so we can rewrite them atomically alongside
  // the per-business writes.
  const readIds = async (sanitised) => {
    try {
      const snap = await get(ref(database, `employees/${sanitised}`));
      if (!snap.exists()) return [];
      const value = snap.val();
      return Array.isArray(value)
        ? value.filter((v) => typeof v === "string" && v.length > 0)
        : Object.values(value || {}).filter(
            (v) => typeof v === "string" && v.length > 0
          );
    } catch (e) {
      return [];
    }
  };

  const [oldIdsRaw, newIdsRaw] = await Promise.all([
    readIds(sanitisedOld),
    sanitisedOld === sanitisedNew ? Promise.resolve([]) : readIds(sanitisedNew),
  ]);
  const oldRemaining = oldIdsRaw.filter((id) => id !== businessId);
  const newIds = (sanitisedOld === sanitisedNew ? [] : newIdsRaw).slice();
  if (!newIds.includes(businessId)) newIds.push(businessId);

  const updates = {
    [`businesses/${businessId}/employeesData`]: nextList,
    [`businesses/${businessId}/employees/${sanitisedOld}`]: null,
    [`businesses/${businessId}/employees/${sanitisedNew}`]: true,
    [`employees/${sanitisedOld}`]:
      oldRemaining.length === 0 ? null : oldRemaining,
    [`employees/${sanitisedNew}`]: newIds,
  };
  await update(ref(database), updates);
};

// Reverse of saveEmployeeRecords. Drops the row from `employeesData`,
// nulls `businesses/<id>/employees/<sanitised>` and removes the business
// id from the global `employees/<sanitised>` list (nulls it if empty).
export const deleteEmployeeRecords = async (businessId, employee) => {
  if (!businessId) throw new Error("businessId is required");
  if (!employee || !employee.email) throw new Error("employee.email is required");

  const normalisedEmail = String(employee.email).toLowerCase();
  const sanitisedEmail = sanitiseEmailKey(normalisedEmail);

  const nextList = await buildEmployeesDataUpdate(
    businessId,
    normalisedEmail,
    () => null
  );

  let globalIds = [];
  try {
    const globalRef = ref(database, `employees/${sanitisedEmail}`);
    const snap = await get(globalRef);
    if (snap.exists()) {
      const value = snap.val();
      globalIds = Array.isArray(value)
        ? value.filter((v) => typeof v === "string" && v.length > 0)
        : Object.values(value || {}).filter(
            (v) => typeof v === "string" && v.length > 0
          );
    }
  } catch (e) {
    // Tolerate read miss - the write below will just clear the index.
  }
  const remaining = globalIds.filter((id) => id !== businessId);

  const updates = {
    [`businesses/${businessId}/employeesData`]: nextList,
    [`businesses/${businessId}/employees/${sanitisedEmail}`]: null,
    [`employees/${sanitisedEmail}`]: remaining.length === 0 ? null : remaining,
  };
  await update(ref(database), updates);
};
